import React from 'react';
import { Twitter, Github, MessageSquare, ExternalLink } from 'lucide-react';
import { useThemeMode } from '../../hooks/useThemeMode';

const protocolLinks = [
  { label: 'Protocol', href: '#how-it-works' },
  { label: 'Architecture', href: '#features' },
  { label: 'Ecosystem', href: '#chains' },
  { label: 'Agents', href: '#agents' },
];

const resourceLinks = [
  { label: 'Whitepaper', href: '/technical-paper' },
  { label: 'Docs', href: '/docs' },
  { label: 'llms.txt', href: '/llms.txt' },
  { label: 'MCP Docs', href: '/mcp.md' },
];

const socialLinks = [
  { icon: Twitter, href: 'https://x.com/BlackBoxCore', label: 'Twitter' },
  { icon: Github, href: 'https://github.com/InterNullOrg', label: 'GitHub' },
  { icon: MessageSquare, href: 'https://discord.com/invite/yrVukgBp72', label: 'Discord' },
];

const Footer = () => {
  const { isDark } = useThemeMode();

  return (
    <footer
      className={`border-t pt-20 pb-10 ${isDark ? 'border-neutral-900' : 'border-neutral-200'}`}
      style={{ backgroundColor: isDark ? '#050505' : '#fafafa' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Top section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/logo/main-logo.png`}
                alt=""
                aria-hidden="true"
                className="h-10 w-10"
                style={{ objectFit: 'contain' }}
              />
              <img
                src={`${process.env.PUBLIC_URL}/logo/${isDark ? 'minimalist-dark.png' : 'minimalist-light.png'}`}
                alt="InterNull"
                className="h-7"
                style={{ objectFit: 'contain' }}
              />
            </div>
            <p className={`text-sm max-w-xs leading-relaxed ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>
              Next generation privacy protocol for the multi-chain future.
            </p>
            <div className="flex gap-3 mt-6">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2.5 rounded-full transition-all duration-200 ${
                    isDark
                      ? 'text-neutral-500 hover:text-white hover:bg-white/[0.06]'
                      : 'text-neutral-400 hover:text-neutral-900 hover:bg-black/[0.04]'
                  }`}
                  aria-label={label}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Protocol links */}
          <div>
            <h4 className={`text-xs font-mono font-semibold tracking-widest uppercase mb-4 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Protocol</h4>
            <ul className="space-y-2.5">
              {protocolLinks.map(link => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className={`text-sm font-mono transition-colors duration-200 ${
                      isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-400 hover:text-neutral-900'
                    }`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resource links */}
          <div>
            <h4 className={`text-xs font-mono font-semibold tracking-widest uppercase mb-4 ${isDark ? 'text-neutral-400' : 'text-neutral-500'}`}>Resources</h4>
            <ul className="space-y-2.5">
              {resourceLinks.map(link => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className={`text-sm font-mono flex items-center gap-1.5 transition-colors duration-200 ${
                      isDark ? 'text-neutral-500 hover:text-white' : 'text-neutral-400 hover:text-neutral-900'
                    }`}
                  >
                    {link.label}
                    <ExternalLink size={12} className="opacity-40" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className={`border-t pt-8 flex flex-col md:flex-row justify-between text-xs font-mono ${isDark ? 'border-neutral-900 text-neutral-600' : 'border-neutral-200 text-neutral-400'}`}>
          <p>&copy; {new Date().getFullYear()} InterNull Protocol. All rights reserved.</p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="#" className={`transition-colors ${isDark ? 'hover:text-neutral-400' : 'hover:text-neutral-600'}`}>Privacy Policy</a>
            <a href="#" className={`transition-colors ${isDark ? 'hover:text-neutral-400' : 'hover:text-neutral-600'}`}>Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
